
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';

interface SubscriberData {
  id: string;
  name: string;
  email: string;
  country: string;
  industry?: string;
  subscription_date: string;
  last_access: string;
  total_views: number;
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscriberData, setSubscriberData] = useState<SubscriberData | null>(null);
  const [subscriberLoading, setSubscriberLoading] = useState(true);

  // Cookie helpers for 30-day persistence
  const setCookie = (name: string, value: string, days: number = 30) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
    console.log('🍪 Cookie set:', name, 'expires:', expires.toUTCString());
  };

  const getCookie = (name: string): string | null => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        const value = decodeURIComponent(c.substring(nameEQ.length, c.length));
        console.log('🍪 Cookie found:', name, value);
        return value;
      }
    }
    console.log('🍪 Cookie not found:', name);
    return null;
  };

  const deleteCookie = (name: string) => {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    console.log('🍪 Cookie deleted:', name);
  };

  const checkSubscriberStatus = async (email: string) => {
    try {
      setSubscriberLoading(true);
      console.log('🔍 Checking subscriber status for:', email);
      
      const { data, error } = await supabase
        .from('subscribers')
        .select('*')
        .eq('email', email.toLowerCase())
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking subscriber status:', error);
        setSubscriberData(null);
      } else if (data) {
        console.log('✅ Subscriber found:', data);
        const subscriberInfo = {
          id: data.id,
          email: data.email,
          name: data.name,
          country: data.country,
          industry: data.industry,
          subscription_date: data.subscription_date,
          last_access: data.last_access,
          total_views: data.total_views || 0
        };
        
        setSubscriberData(subscriberInfo);
        
        // Store in cookie for persistence
        setCookie('dbd_subscriber', JSON.stringify(subscriberInfo), 30);
        
        // Create mock user for authentication state
        const mockUser = {
          id: data.id,
          email: data.email,
          user_metadata: {
            name: data.name
          }
        } as User;
        
        setUser(mockUser);
      } else {
        console.log('❌ No subscriber found');
        setSubscriberData(null);
        setUser(null);
        deleteCookie('dbd_subscriber');
      }
    } catch (error) {
      console.error('Error checking subscriber status:', error);
      setSubscriberData(null);
      setUser(null);
    } finally {
      setSubscriberLoading(false);
    }
  };

  const loadFromCookie = () => {
    const cookieData = getCookie('dbd_subscriber');
    if (cookieData) {
      try {
        const parsed = JSON.parse(cookieData);
        console.log('🍪 Loading subscriber from cookie:', parsed);
        
        setSubscriberData(parsed);
        
        // Create mock user for authentication state
        const mockUser = {
          id: parsed.id,
          email: parsed.email,
          user_metadata: {
            name: parsed.name
          }
        } as User;
        
        setUser(mockUser);
        setSubscriberLoading(false);
        setLoading(false);
        return true;
      } catch (error) {
        console.error('Error parsing cookie data:', error);
        deleteCookie('dbd_subscriber');
      }
    }
    return false;
  };

  useEffect(() => {
    console.log('🚀 useAuth initialization started');
    
    // Try to load from cookie first
    const hasValidCookie = loadFromCookie();
    
    if (!hasValidCookie) {
      // Get initial session from Supabase Auth
      supabase.auth.getSession().then(({ data: { session } }) => {
        console.log('📡 Supabase session:', session);
        
        if (session?.user?.email) {
          setUser(session.user);
          checkSubscriberStatus(session.user.email);
        } else {
          setUser(null);
          setSubscriberData(null);
          setSubscriberLoading(false);
        }
        setLoading(false);
      });

      // Listen for auth changes
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, session) => {
        console.log('🔄 Auth state changed:', _event, session);
        
        if (session?.user?.email) {
          setUser(session.user);
          checkSubscriberStatus(session.user.email);
        } else {
          setUser(null);
          setSubscriberData(null);
          setSubscriberLoading(false);
          deleteCookie('dbd_subscriber');
        }
        setLoading(false);
      });

      return () => subscription.unsubscribe();
    }
  }, []);

  const loginWithSubscriberData = (data: SubscriberData) => {
    console.log('🔐 Logging in subscriber with data:', data);
    
    setSubscriberData(data);
    
    // Store in cookie for 30-day persistence
    setCookie('dbd_subscriber', JSON.stringify(data), 30);
    
    // Create a mock user object for authentication
    const mockUser = {
      id: data.id,
      email: data.email,
      user_metadata: {
        name: data.name
      }
    } as User;
    
    setUser(mockUser);
    setLoading(false);
    setSubscriberLoading(false);
    
    console.log('✅ Login completed, states updated');
  };

  const signOut = async () => {
    console.log('🚪 Signing out...');
    
    await supabase.auth.signOut();
    setSubscriberData(null);
    setUser(null);
    deleteCookie('dbd_subscriber');
    
    console.log('✅ Sign out completed');
  };

  const refreshSubscriberStatus = async () => {
    if (subscriberData?.email) {
      await checkSubscriberStatus(subscriberData.email);
    }
  };

  // Current authentication state
  const isAuthenticated = !!subscriberData || !!user;
  const isSubscriber = !!subscriberData;
  const hasAccess = !!subscriberData;

  console.log('🔍 Current auth state:', {
    user: !!user,
    subscriberData: !!subscriberData,
    isAuthenticated,
    isSubscriber,
    hasAccess,
    loading,
    subscriberLoading
  });

  return {
    user: subscriberData ? {
      id: subscriberData.id,
      email: subscriberData.email,
      user_metadata: {
        name: subscriberData.name
      }
    } as User : user,
    subscriberData,
    loading,
    signOut,
    refreshSubscriberStatus,
    loginWithSubscriberData,
    isAuthenticated,
    isSubscriber,
    subscriberLoading,
    hasAccess
  };
};
